import { Comparison } from '@/features/api';
import {
  DateProperty,
  DropdownProperty,
  NumberProperty,
  PropertyType,
  TextProperty,
} from '@/legacy/shared/components/SearchFilter/models/Property';

import PayablesTableRowData from '../models/payablesTableRow';
import PayablesTableSearchQuery from '../models/payablesTableSearchQuery';

import PayablesTableQuery, { PayablesTableQueryType } from './PayablesTable.queries';

/**
 * Properties required to render a table component for Payables
 */
interface PayablesTableProperties {
  readonly paymentStatus: DropdownProperty;
  readonly amount: NumberProperty;
  readonly requestedDebitDate: DateProperty;
  readonly createdDate: DateProperty;
  readonly debitDate: DateProperty;
  readonly paymentMethod: DropdownProperty;
  readonly bank: DropdownProperty;
  readonly memo: TextProperty;
  readonly initiatedBy: DropdownProperty;
  readonly recurring: DropdownProperty;
  readonly currency: DropdownProperty;
  readonly destinationCountry: DropdownProperty;
  readonly imported: DropdownProperty;
}

/**
 * Properties required to manage table component state for Payables Table
 */
interface PayablesTableState {
  /**
   * Required properties to render payables table component
   */
  readonly properties: PayablesTableProperties;
  readonly propertiesOrder: Array<keyof PayablesTableProperties>;
  readonly query: PayablesTableSearchQuery;
  readonly totalNumberOfRecords: number;
  readonly grandTotalNumberOfRecords: number;
  readonly rows: Array<PayablesTableRowData>;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly filterStatus: 'not loaded' | 'loading' | 'loaded';
  readonly queries: Array<PayablesTableQuery>;
}

const pendingPayablesStatusOptions = [
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'InClearing', label: 'In Clearing' },
  { value: 'ApprovalRequired', label: 'Approval Required' },
];

const completedPayablesStatusOptions = [
  { value: 'Deleted', label: 'Deleted' },
  { value: 'Completed', label: 'Completed' },
  { value: 'DebitFailed', label: 'Debit Failed' },
  { value: 'CreditFailed', label: 'Credit Failed' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Recalled', label: 'Recalled' },
  { value: 'CheckMailed', label: 'Check Mailed' },
];

const getInitialState = (
  pendingPayables: boolean,
  recentPayablesContactId: string
): PayablesTableState => {
  const initialQuery: PayablesTableSearchQuery = {
    searchText: '',
    filters: [],
    sortDirections: {
      contact: false,
      status: false,
      requestedDebitDate: false,
      estimatedDeliveryDate: false,
      amount: false,
    },
    sortOrder: [],
    pageIndex: 0,
    pageSize: 10,
    pendingPayables,
    recentPayablesContactId,
  };

  return {
    properties: {
      paymentStatus: {
        name: 'Status',
        label: 'Payment Status',
        type: PropertyType.Dropdown,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
        ],
        values: pendingPayables ? pendingPayablesStatusOptions : completedPayablesStatusOptions,
      },
      amount: {
        name: 'Amount',
        label: 'Amount',
        type: PropertyType.Number,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
          { value: Comparison.LessThan, label: 'less than' },
          { value: Comparison.GreaterThan, label: 'greater than' },
          { value: Comparison.LessOrEqual, label: 'less or equal' },
          { value: Comparison.GreaterOrEqual, label: 'greater or equal' },
        ],
      },
      requestedDebitDate: {
        name: 'DebitDate',
        label: 'Requested Debit Date',
        type: PropertyType.Date,
      },
      createdDate: { name: 'CreatedDate', label: 'Created Date', type: PropertyType.Date },
      debitDate: { name: 'WithdrawnOn', label: 'Debit Date', type: PropertyType.Date },
      paymentMethod: {
        name: 'Method',
        label: 'Payment Method',
        type: PropertyType.Dropdown,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
        ],
        values: [
          { value: '0', label: 'Bank Deposit' },
          { value: '1', label: 'Cheque' },
          { value: '2', label: 'Email Money Transfer' },
        ],
      },
      bank: {
        name: 'Bank',
        label: 'Account',
        type: PropertyType.Dropdown,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
        ],
        values: [],
        loading: true,
      },
      memo: {
        name: 'Memo',
        label: 'Memo',
        type: PropertyType.Text,
        relations: [{ value: Comparison.Contains, label: 'contains' }],
      },
      initiatedBy: {
        name: 'InitiatedBy',
        label: 'Initiated By',
        type: PropertyType.Dropdown,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
        ],
        values: [],
        loading: true,
      },
      recurring: {
        name: 'Recurring',
        label: 'Recurring',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ],
      },
      currency: {
        name: 'Currency',
        label: 'Currency',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [],
        loading: true,
      },
      destinationCountry: {
        name: 'DestinationCountry',
        label: 'Destination Country',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [],
        loading: true,
      },
      imported: {
        name: 'Imported',
        label: 'Imported',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ],
      },
    },
    propertiesOrder: [
      'paymentStatus',
      'amount',
      'requestedDebitDate',
      'createdDate',
      'debitDate',
      'paymentMethod',
      'bank',
      'memo',
      'initiatedBy',
      'recurring',
      'currency',
      'destinationCountry',
      'imported',
    ],
    query: initialQuery,
    rows: [],
    totalNumberOfRecords: undefined,
    grandTotalNumberOfRecords: null,
    isLoading: true,
    isError: false,
    filterStatus: 'not loaded',
    queries: [
      {
        type: PayablesTableQueryType.GetPage,
        query: initialQuery,
      },
    ],
  };
};

export { PayablesTableProperties, getInitialState };
export default PayablesTableState;
